import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { learningPathApi } from '@/utils/api'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Plus, Upload, Edit, Trash2, Video, Image as ImageIcon, Play } from 'lucide-react'
import { resolveBackendUrl } from '@/utils/media'

interface Module {
  id: string
  title: string
  description?: string
  category: string
  orderIndex: number
  thumbnail?: string
  estimatedDuration: number
  isRecommended: boolean
  isActive: boolean
  videos?: VideoItem[]
}

interface VideoItem {
  id: string
  moduleId: string
  title: string
  description?: string
  videoUrl: string
  thumbnail?: string
  duration: number
  orderIndex: number
}

const MODULE_CATEGORIES = [
  'sexual_desire',
  'coercive_control',
  'financial_coercion',
  'gender_role_hierarchy',
  'substance_use_acute_risk',
  'role_challenge_supported_abuse',
  'communication_anger_dysregulation',
  'emotional_instability_dysfunction',
]

export default function AdminLearningPathPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)

  const [modules, setModules] = useState<Module[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingModule, setEditingModule] = useState<Module | null>(null)
  const [uploadingThumbnail, setUploadingThumbnail] = useState<string | null>(null)
  const [uploadingVideo, setUploadingVideo] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [previewVideoId, setPreviewVideoId] = useState<string | null>(null)

  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    category: '',
    orderIndex: 0,
    estimatedDuration: 0,
    isRecommended: false,
  })

  const [videoForm, setVideoForm] = useState({
    category: '',
    title: '',
    description: '',
    videoUrl: '',
    duration: 0,
    orderIndex: 0,
  })

  const [showVideoForm, setShowVideoForm] = useState(false)
  const [uploadMode, setUploadMode] = useState<'url' | 'file'>('url')
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)
  const [extractingDuration, setExtractingDuration] = useState(false)

  useEffect(() => {
    loadModules()
  }, [])

  const loadModules = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await learningPathApi.listModules()
      const data = (response as { data?: Module[] }).data || (response as unknown as Module[])
      setModules(Array.isArray(data) ? data : [])
    } catch (err) {
      setError((err as Error)?.message || 'Failed to load modules')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateModule = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingModule) return

    try {
      await learningPathApi.updateModule(editingModule.id, moduleForm)
      setEditingModule(null)
      setModuleForm({
        title: '',
        description: '',
        category: '',
        orderIndex: 0,
        estimatedDuration: 0,
        isRecommended: false,
      })
      loadModules()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to update module')
    }
  }

  const handleDeleteModule = async (moduleId: string) => {
    try {
      await learningPathApi.deleteModule(moduleId)
      setDeleteConfirm(null)
      loadModules()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to delete module')
    }
  }

  const handleUploadThumbnail = async (moduleId: string, file: File) => {
    try {
      setUploadingThumbnail(moduleId)
      const response = await learningPathApi.uploadThumbnail(moduleId, file)
      const fileData = (response as { data?: { url: string } }).data

      if (fileData?.url) {
        await learningPathApi.updateModule(moduleId, { thumbnail: fileData.url })
      }

      loadModules()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to upload thumbnail')
    } finally {
      setUploadingThumbnail(null)
    }
  }

  const handleUploadVideo = async (videoId: string, file: File) => {
    try {
      setUploadingVideo(videoId)
      const response = await learningPathApi.uploadVideo(videoId, file)
      const fileData = (response as { data?: { url: string } }).data

      if (fileData?.url) {
        await learningPathApi.updateVideo(videoId, { videoUrl: fileData.url })
      }

      loadModules()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to upload video')
    } finally {
      setUploadingVideo(null)
    }
  }

  const handleAddVideo = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      let finalVideoUrl = videoForm.videoUrl
      let finalThumbnailUrl = ''

      // If file upload mode and file selected, upload the video file first
      if (uploadMode === 'file' && videoFile) {
        const videoResponse = await learningPathApi.uploadVideo('', videoFile)
        const videoData = (videoResponse as { data?: { url: string } }).data
        if (videoData?.url) {
          finalVideoUrl = videoData.url
        }
      }

      // If thumbnail file selected, upload thumbnail
      if (thumbnailFile) {
        const thumbnailResponse = await learningPathApi.uploadThumbnail('', thumbnailFile)
        const thumbnailData = (thumbnailResponse as { data?: { url: string } }).data
        if (thumbnailData?.url) {
          finalThumbnailUrl = thumbnailData.url
        }
      }

      // Use the new category-based endpoint
      const videoData: Record<string, unknown> = {
        title: videoForm.title,
        videoUrl: finalVideoUrl || 'pending-upload',
        duration: videoForm.duration,
        orderIndex: videoForm.orderIndex,
      }
      if (videoForm.description) videoData.description = videoForm.description
      if (finalThumbnailUrl) videoData.thumbnail = finalThumbnailUrl

      await learningPathApi.addVideoToCategory(videoForm.category, videoData as {
        title: string
        description?: string
        videoUrl: string
        duration?: number
        orderIndex?: number
      })

      setShowVideoForm(false)
      setVideoForm({
        category: '',
        title: '',
        description: '',
        videoUrl: '',
        duration: 0,
        orderIndex: 0,
      })
      setVideoFile(null)
      setThumbnailFile(null)
      setThumbnailPreview(null)
      setUploadMode('url')
      loadModules()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to add video')
    }
  }

  const handleThumbnailChange = (file: File) => {
    setThumbnailFile(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      setThumbnailPreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const extractVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.preload = 'metadata'

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src)
        const duration = Math.round(video.duration)
        resolve(duration)
      }

      video.onerror = () => {
        reject(new Error('Failed to load video metadata'))
      }

      video.src = URL.createObjectURL(file)
    })
  }

  const handleVideoFileChange = async (file: File) => {
    if (file.size > 500 * 1024 * 1024) {
      setError('Video file must be less than 500MB')
      return
    }

    setVideoFile(file)
    setExtractingDuration(true)

    try {
      const duration = await extractVideoDuration(file)
      setVideoForm(prev => ({ ...prev, duration }))
    } catch {
      setError('Could not extract video duration. Please enter manually.')
    } finally {
      setExtractingDuration(false)
    }
  }

  const startEditModule = (module: Module) => {
    setEditingModule(module)
    setModuleForm({
      title: module.title,
      description: module.description || '',
      category: module.category,
      orderIndex: module.orderIndex,
      estimatedDuration: module.estimatedDuration,
      isRecommended: module.isRecommended,
    })
  }

  const openAddVideoForCategory = (category: string, existingVideoCount: number) => {
    setVideoForm({
      category,
      title: '',
      description: '',
      videoUrl: '',
      duration: 0,
      orderIndex: existingVideoCount,
    })
    setShowVideoForm(true)
  }

  const handleSignOut = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title='Learning Path Management'
          infoText='Manage learning modules and upload videos for each category.'
          showLocation={false}
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
      </div>

      {error && (
        <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg'>
          {error}
        </div>
      )}

      <div className='flex justify-between items-center'>
        <h2 className='text-2xl font-bold text-gray-900'>Learning Modules</h2>
        <Sheet open={showVideoForm} onOpenChange={setShowVideoForm}>
          <SheetTrigger asChild>
            <Button
              className='bg-orange-600 hover:bg-orange-700'
              onClick={() => setVideoForm({
                category: '',
                title: '',
                description: '',
                videoUrl: '',
                duration: 0,
                orderIndex: 0,
              })}
            >
              <Plus className='h-4 w-4 mr-2' />
              Add Video
            </Button>
          </SheetTrigger>
          <SheetContent className='overflow-y-auto'>
            <SheetHeader>
              <SheetTitle>Add Video to Category</SheetTitle>
            </SheetHeader>
            <form onSubmit={handleAddVideo} className='space-y-4 mt-6'>
              {/* Category Selector */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Category *
                </label>
                <select
                  required
                  value={videoForm.category}
                  onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                >
                  <option value=''>Select category</option>
                  {MODULE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
                <p className='text-xs text-gray-500 mt-1'>The video will be added to this category's module automatically.</p>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>Title *</label>
                <input
                  type='text'
                  required
                  value={videoForm.title}
                  onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>Description</label>
                <textarea
                  value={videoForm.description}
                  onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                  rows={2}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                />
              </div>

              {/* Thumbnail Upload */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Video Thumbnail (Optional)</label>
                {thumbnailPreview ? (
                  <div className='relative w-full h-32 mb-2'>
                    <img
                      src={thumbnailPreview}
                      alt='Thumbnail preview'
                      className='w-full h-full object-cover rounded-lg'
                    />
                    <button
                      type='button'
                      onClick={() => {
                        setThumbnailFile(null)
                        setThumbnailPreview(null)
                      }}
                      className='absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700'
                    >
                      <Trash2 className='h-4 w-4' />
                    </button>
                  </div>
                ) : (
                  <label className='flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50'>
                    <div className='flex flex-col items-center justify-center pt-5 pb-6'>
                      <ImageIcon className='h-8 w-8 text-gray-400 mb-2' />
                      <p className='text-xs text-gray-500'>Click to upload thumbnail</p>
                      <p className='text-xs text-gray-400'>JPG, PNG, GIF (max 5MB)</p>
                    </div>
                    <input
                      type='file'
                      accept='image/jpeg,image/jpg,image/png,image/gif'
                      className='hidden'
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            setError('Thumbnail must be less than 5MB')
                            return
                          }
                          handleThumbnailChange(file)
                        }
                      }}
                    />
                  </label>
                )}
              </div>

              {/* Upload Mode Toggle */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Video Source *</label>
                <div className='flex gap-2 mb-3'>
                  <button
                    type='button'
                    onClick={() => {
                      setUploadMode('url')
                      setVideoFile(null)
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      uploadMode === 'url'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    URL
                  </button>
                  <button
                    type='button'
                    onClick={() => {
                      setUploadMode('file')
                      setVideoForm({ ...videoForm, videoUrl: '' })
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      uploadMode === 'file'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Upload File
                  </button>
                </div>

                {uploadMode === 'url' ? (
                  <div>
                    <input
                      type='url'
                      required
                      value={videoForm.videoUrl}
                      onChange={(e) => setVideoForm({ ...videoForm, videoUrl: e.target.value })}
                      placeholder='https://example.com/video.mp4'
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                    />
                    <p className='text-xs text-gray-500 mt-1'>Enter a direct video URL</p>
                  </div>
                ) : (
                  <div>
                    {videoFile ? (
                      <div className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
                        <div className='flex items-center gap-2'>
                          <Video className='h-5 w-5 text-gray-500' />
                          <div>
                            <p className='text-sm font-medium text-gray-900'>{videoFile.name}</p>
                            <p className='text-xs text-gray-500'>
                              {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type='button'
                          onClick={() => setVideoFile(null)}
                          className='text-red-600 hover:text-red-700'
                        >
                          <Trash2 className='h-4 w-4' />
                        </button>
                      </div>
                    ) : (
                      <label className='flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50'>
                        <div className='flex flex-col items-center justify-center pt-5 pb-6'>
                          <Upload className='h-6 w-6 text-gray-400 mb-1' />
                          <p className='text-xs text-gray-500'>Click to upload video file</p>
                          <p className='text-xs text-gray-400'>MP4, AVI, MOV, MKV (max 500MB)</p>
                        </div>
                        <input
                          type='file'
                          accept='video/mp4,video/avi,video/mov,video/mkv'
                          className='hidden'
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              handleVideoFileChange(file)
                            }
                          }}
                        />
                      </label>
                    )}
                    {!videoFile && (
                      <p className='text-xs text-gray-500 mt-1'>File will be uploaded when you submit</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Duration (seconds)
                </label>
                <input
                  type='number'
                  value={videoForm.duration}
                  onChange={(e) => setVideoForm({ ...videoForm, duration: parseInt(e.target.value) || 0 })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                  disabled={extractingDuration}
                />
                {extractingDuration && (
                  <p className='text-xs text-blue-600 mt-1'>Extracting duration from video...</p>
                )}
                {uploadMode === 'file' && !extractingDuration && videoForm.duration > 0 && (
                  <p className='text-xs text-green-600 mt-1'>
                    Duration auto-detected: {Math.floor(videoForm.duration / 60)}m {videoForm.duration % 60}s
                  </p>
                )}
                {uploadMode === 'url' && (
                  <p className='text-xs text-gray-500 mt-1'>Enter video duration in seconds (optional)</p>
                )}
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Order Index
                </label>
                <input
                  type='number'
                  value={videoForm.orderIndex}
                  onChange={(e) => setVideoForm({ ...videoForm, orderIndex: parseInt(e.target.value) || 0 })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  Display order within the category. Defaults to 0.
                </p>
              </div>
              <Button type='submit' className='w-full bg-orange-600 hover:bg-orange-700'>
                Add Video
              </Button>
            </form>
          </SheetContent>
        </Sheet>
      </div>

      {loading ? (
        <div className='text-center py-12'>
          <p className='text-gray-600'>Loading modules...</p>
        </div>
      ) : modules.length === 0 ? (
        <div className='text-center py-12'>
          <p className='text-gray-600'>No modules found. Modules are auto-created for each category on server startup.</p>
        </div>
      ) : (
        <div className='grid grid-cols-1 gap-6'>
          {modules.map((module) => (
            <div key={module.id} className='bg-white rounded-xl border border-gray-200 p-6 shadow-sm'>
              <div className='flex items-start justify-between mb-4'>
                <div className='flex-1'>
                  <div className='flex items-center gap-3 mb-2'>
                    <h3 className='text-xl font-bold text-gray-900'>{module.title}</h3>
                    {module.isRecommended && (
                      <span className='px-2 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full'>
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className='text-sm text-gray-600 mb-2'>{module.description}</p>
                  <div className='flex items-center gap-4 text-sm text-gray-500'>
                    <span className='capitalize'>{module.category.replace(/_/g, ' ')}</span>
                    <span>•</span>
                    <span>{module.estimatedDuration} min</span>
                    <span>•</span>
                    <span>{module.videos?.length || 0} videos</span>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => startEditModule(module)}
                  >
                    <Edit className='h-4 w-4' />
                  </Button>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => setDeleteConfirm(module.id)}
                    className='text-red-600 hover:bg-red-50'
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              </div>

              <div className='flex items-center gap-4 mb-4'>
                <div className='relative'>
                  {module.thumbnail ? (
                    <img
                      src={resolveBackendUrl(module.thumbnail)}
                      alt={module.title}
                      className='w-32 h-20 object-cover rounded-lg'
                    />
                  ) : (
                    <div className='w-32 h-20 bg-gray-100 rounded-lg flex items-center justify-center'>
                      <ImageIcon className='h-8 w-8 text-gray-400' />
                    </div>
                  )}
                  <label className='absolute bottom-1 right-1 cursor-pointer'>
                    <input
                      type='file'
                      accept='image/jpeg,image/jpg,image/png,image/gif'
                      className='hidden'
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleUploadThumbnail(module.id, file)
                      }}
                      disabled={uploadingThumbnail === module.id}
                    />
                    <div className='bg-white p-1 rounded-full shadow-md hover:bg-gray-50'>
                      <Upload className='h-3 w-3 text-gray-700' />
                    </div>
                  </label>
                </div>
                <Button
                  size='sm'
                  onClick={() => openAddVideoForCategory(module.category, module.videos?.length || 0)}
                  className='bg-orange-600 hover:bg-orange-700'
                >
                  <Plus className='h-4 w-4 mr-1' />
                  Add Video
                </Button>
              </div>

              {module.videos && module.videos.length > 0 && (
                <div className='space-y-2'>
                  <h4 className='text-sm font-semibold text-gray-700'>Videos</h4>
                  {module.videos.map((video) => (
                    <div key={video.id} className='space-y-3'>
                      <div className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
                        <div className='flex items-center gap-3'>
                          <Video className='h-4 w-4 text-gray-500' />
                          <div>
                            <p className='text-sm font-medium text-gray-900'>{video.title}</p>
                            <p className='text-xs text-gray-500'>
                              {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                            </p>
                          </div>
                        </div>
                        <div className='flex items-center gap-2'>
                          <Button
                            size='sm'
                            variant='outline'
                            type='button'
                            disabled={!video.videoUrl}
                            onClick={() =>
                              setPreviewVideoId((current) => (current === video.id ? null : video.id))
                            }
                          >
                            <Play className='h-4 w-4 mr-1' />
                            {previewVideoId === video.id ? 'Hide' : 'Preview'}
                          </Button>
                          <label className='cursor-pointer'>
                            <input
                              type='file'
                              accept='video/mp4,video/avi,video/mov,video/mkv'
                              className='hidden'
                              onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) handleUploadVideo(video.id, file)
                              }}
                              disabled={uploadingVideo === video.id}
                            />
                            <Button size='sm' variant='outline' disabled={uploadingVideo === video.id}>
                              <Upload className='h-4 w-4 mr-1' />
                              {uploadingVideo === video.id ? 'Uploading...' : 'Upload'}
                            </Button>
                          </label>
                        </div>
                      </div>
                      {previewVideoId === video.id && video.videoUrl && (
                        <div className='rounded-lg border border-gray-200 overflow-hidden bg-black'>
                          <video
                            className='w-full'
                            controls
                            poster={video.thumbnail ? resolveBackendUrl(video.thumbnail) : undefined}
                            src={resolveBackendUrl(video.videoUrl)}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Module Sheet */}
      <Sheet open={!!editingModule} onOpenChange={(open) => !open && setEditingModule(null)}>
        <SheetContent className='overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>Edit Module</SheetTitle>
          </SheetHeader>
          <form onSubmit={handleUpdateModule} className='space-y-4 mt-6'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Title *</label>
              <input
                type='text'
                required
                value={moduleForm.title}
                onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Description</label>
              <textarea
                value={moduleForm.description}
                onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                rows={3}
                className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Category</label>
              <input
                type='text'
                disabled
                value={moduleForm.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                className='w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500'
              />
              <p className='text-xs text-gray-500 mt-1'>Category is set automatically and cannot be changed.</p>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Order Index *</label>
              <input
                type='number'
                required
                value={moduleForm.orderIndex}
                onChange={(e) => setModuleForm({ ...moduleForm, orderIndex: parseInt(e.target.value) })}
                className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Estimated Duration (minutes) *
              </label>
              <input
                type='number'
                required
                value={moduleForm.estimatedDuration}
                onChange={(e) => setModuleForm({ ...moduleForm, estimatedDuration: parseInt(e.target.value) })}
                className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
              />
            </div>
            <div className='flex items-center gap-2'>
              <input
                type='checkbox'
                id='editIsRecommended'
                checked={moduleForm.isRecommended}
                onChange={(e) => setModuleForm({ ...moduleForm, isRecommended: e.target.checked })}
                className='h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded'
              />
              <label htmlFor='editIsRecommended' className='text-sm font-medium text-gray-700'>
                Recommended Module
              </label>
            </div>
            <Button type='submit' className='w-full bg-gray-900 hover:bg-gray-800'>
              Update Module
            </Button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-xl p-6 max-w-md w-full mx-4'>
            <h3 className='text-lg font-bold text-gray-900 mb-2'>Delete Module?</h3>
            <p className='text-sm text-gray-600 mb-6'>
              Are you sure you want to delete this module? This action cannot be undone and will also delete all associated videos.
            </p>
            <div className='flex gap-3'>
              <Button
                variant='outline'
                onClick={() => setDeleteConfirm(null)}
                className='flex-1'
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteModule(deleteConfirm)}
                className='flex-1 bg-red-600 hover:bg-red-700'
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
